import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import styled from '@emotion/styled';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CallIcon from '@mui/icons-material/Call';

// Styled components
const ContactSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#101010', // Fallback color in case image doesn't load
  backgroundImage: 'url(/assets/images/bg/contact-bg.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  padding: '100px 0',
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
  },
  [theme.breakpoints.down('md')]: {
    padding: '70px 0',
  }
}));

const ContactContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: '60px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '40px',
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  color: '#ffff',
  position: 'relative',
  paddingBottom: '20px',
  '&:after': {
    content: '""',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 0,
    height: '5px',
    width: '90px',
    backgroundColor: theme.palette.primary.main,
  }
}));

const ContactForm = styled(Paper)(({ theme }) => ({
  padding: '30px',
  borderRadius: '0',
  boxShadow: '0 5px 20px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#1a1a1a',
  color: '#fff',
}));

const FormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: '30px',
  fontWeight: 600,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: '20px',
  '& .MuiInputBase-input': {
    color: '#fff',
  },
  '& .MuiInputLabel-root': {
    color: '#fff',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#333',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
    backgroundColor: '#1a1a1a',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#ffff',
  padding: '12px 30px',
  borderRadius: '0',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#d30000',
  }
}));

const MapSection = styled(Box)(({ theme }) => ({
  backgroundImage: 'url("assets/images/bg/choose-bg.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  padding: `${theme.spacing(12)} 0`,
  position: 'relative',
  // Background overlay removed to show the actual background image
  [theme.breakpoints.down('md')]: {
    padding: `${theme.spacing(7)} 0`,
  }
}));

const MapTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: '40px',
  fontWeight: 600,
  color: '#333',
  position: 'relative',
  paddingBottom: '20px',
  '&:after': {
    content: '""',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 0,
    height: '3px',
    width: '60px',
    backgroundColor: theme.palette.primary.main,
  }
}));

const MapContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  '& iframe': {
    borderRadius: '10px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      height: '350px',
    },
    [theme.breakpoints.down('sm')]: {
      height: '300px',
    }
  }
}));

const ContactInfoSection = styled(Box)(({ theme }) => ({
  padding: '20px 0',
  [theme.breakpoints.down('md')]: {
    padding: '20px 0',
  }
}));

const ContactCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#1a1a1a',
  color: '#fff',
  borderRadius: 0,
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '15px'
}));

const ContactIcon = styled(Box)(({ theme }) => ({
  '& svg': {
    color: theme.palette.primary.main,
    fontSize: '2rem'
  }
}));

const ContactTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#fff',
  margin: 0
}));

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      console.log('Form submitted:', formData);
      // Reset form after successful submission
      setFormData({
        name: '',
        surname: '',
        email: '',
        phone: '',
        message: ''
      });
      setIsSubmitting(false);
      setOpenSnackbar(true);
    }, 1000);
  };
  
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <>
      <ContactSection id="rs-contact">
        <ContactContainer maxWidth="lg">
          <Typography variant="h2" sx={{ textAlign: 'center', color: '#fff', mb: 5 }}>
            Get In Touch With Us
          </Typography>
          
          <Grid container spacing={4}>
            {/* Left side - Map */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: '100%' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d423.05033147510125!2d114.15461282250261!3d22.27970278804372!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3404007acff16b2f%3A0xb3954623e961f4ee!2sWah%20Cheong%20Building%2C%201%20Glenealy%2C%20Central%2C%20Hong%20Kong!5e0!3m2!1sen!2sau!4v1710141059993!5m2!1sen!2sau"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '400px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Our Location - Wah Cheong Building, Central, Hong Kong"
                />
              </Box>
            </Grid>
            
            {/* Right side - Contact Form */}
            <Grid item xs={12} md={6}>
              <ContactForm elevation={0}>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        placeholder="Name"
                        name="name"
                        variant="outlined"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        placeholder="Surname"
                        name="surname"
                        variant="outlined"
                        value={formData.surname || ''}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        placeholder="E-Mail"
                        name="email"
                        variant="outlined"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        placeholder="Phone Number"
                        name="phone"
                        variant="outlined"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        placeholder="Your message Here"
                        name="message"
                        variant="outlined"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SubmitButton 
                        type="submit" 
                        variant="contained"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : 'Submit Enquiry'}
                      </SubmitButton>
                    </Grid>
                  </Grid>
                </form>
              </ContactForm>
            </Grid>
          </Grid>
        </ContactContainer>
        
        {/* Contact Info Cards in a row */}
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <ContactCard elevation={0}>
                <ContactIcon>
                  <LocationOnIcon />
                </ContactIcon>
                <Box>
                  <ContactTitle variant="h6">
                    Headquarter
                  </ContactTitle>
                  <Typography variant="body2" sx={{ color: '#aaa' }}>
                    Unit 2A, 17/F, Glenealy Tower, No.1 Glenealy, Central, Hong Kong S.A.R
                  </Typography>
                </Box>
              </ContactCard>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <ContactCard elevation={0}>
                <ContactIcon>
                  <EmailIcon />
                </ContactIcon>
                <Box>
                  <ContactTitle variant="h6">
                    Email us
                  </ContactTitle>
                  <Typography variant="body2" sx={{ color: '#aaa' }}>
                    info@hkmsp.com
                  </Typography>
                </Box>
              </ContactCard>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <ContactCard elevation={0}>
                <ContactIcon>
                  <CallIcon />
                </ContactIcon>
                <Box>
                  <ContactTitle variant="h6">
                    Call us
                  </ContactTitle>
                  <Typography variant="body2" sx={{ color: '#aaa' }}>
                    (+1) 213-349-8776
                  </Typography>
                </Box>
              </ContactCard>
            </Grid>
          </Grid>
        </Container>
      </ContactSection>
      
      {/* Success message */}
      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Your message has been sent successfully!
        </Alert>
      </Snackbar>
    </>
  );
}

export default Contact;